import type { FastifyRequest, FastifyReply } from 'fastify'
import { SearchService } from './search.service.js'
import type { SearchQuery } from '@my-roadtrip/types'
import { ApiResponse } from '../../utils/response'

export class SearchController {
  private readonly service = new SearchService()

  async search(request: FastifyRequest<{ Querystring: Record<string, string> }>, reply: FastifyReply) {
    try {
      // Sanitize and validate query string (max 200 chars)
      const sanitizedQ = (request.query.q || '').trim().substring(0, 200)

      // Parse and validate pagination parameters
      const parsedPage = Number.parseInt(request.query.page || '1', 10)
      const parsedLimit = Number.parseInt(request.query.limit || '20', 10)

      const query: SearchQuery = {
        q: sanitizedQ,
        page: !Number.isNaN(parsedPage) && parsedPage > 0 ? parsedPage : 1,
        limit: !Number.isNaN(parsedLimit) && parsedLimit > 0 && parsedLimit <= 100 ? parsedLimit : 20,
        filters: {}
      }

      if (request.query.countries) {
        // Validate ISO country codes (2 uppercase letters)
        query.filters!.countries = request.query.countries
          .split(',')
          .map(c => c.trim().toUpperCase())
          .filter(c => /^[A-Z]{2}$/.test(c))
          .slice(0, 10) // Max 10 countries
      }
      if (request.query.tags) {
        // Sanitize tags (alphanumeric + hyphens only)
        query.filters!.tags = request.query.tags
          .split(',')
          .map(t => t.trim().toLowerCase())
          .filter(t => /^[a-z0-9-]+$/.test(t) && t.length <= 50)
          .slice(0, 20) // Max 20 tags
      }
      if (request.query.minDistance) {
        const parsed = Number.parseInt(request.query.minDistance, 10)
        if (!Number.isNaN(parsed) && parsed >= 0) {
          query.filters!.minDistance = parsed
        }
      }
      if (request.query.maxDistance) {
        const parsed = Number.parseInt(request.query.maxDistance, 10)
        if (!Number.isNaN(parsed) && parsed >= 0) {
          query.filters!.maxDistance = parsed
        }
      }
      if (request.query.minDuration) {
        const parsed = Number.parseInt(request.query.minDuration, 10)
        if (!Number.isNaN(parsed) && parsed >= 0) {
          query.filters!.minDuration = parsed
        }
      }
      if (request.query.maxDuration) {
        const parsed = Number.parseInt(request.query.maxDuration, 10)
        if (!Number.isNaN(parsed) && parsed >= 0) {
          query.filters!.maxDuration = parsed
        }
      }

      const results = await this.service.search(query)
      return reply.send(results)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Search failed')
    }
  }

  async searchPOIs(request: FastifyRequest<{ Querystring: { q: string } }>, reply: FastifyReply) {
    try {
      const q = (request.query.q || '').trim()
      if (!q || q.length < 2) {
        return reply.send([])
      }

      const results = await this.service.searchPOIs(q)
      return reply.send(results)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'POI search failed')
    }
  }

  async getFeed(request: FastifyRequest, reply: FastifyReply) {
    try {
      const feed = await this.service.getDiscoveryFeed()
      return reply.send(feed)
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to load feed')
    }
  }

  async indexPlan(request: FastifyRequest<{ Params: { planId: string } }>, reply: FastifyReply) {
    try {
      const { planId } = request.params
      await this.service.indexPlan(planId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to index plan')
    }
  }

  async removePlan(request: FastifyRequest<{ Params: { planId: string } }>, reply: FastifyReply) {
    try {
      const { planId } = request.params
      await this.service.removePlan(planId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to remove plan')
    }
  }
}
